import { FastField } from 'formik'
import React from 'react'
import { CustomCheckboxWithoutInitialValue, CustomTextField } from 'src/form'
import BillingCodes from 'src/participantHistory/intakeNote/BillingCodes'
import Interventions from './Interventions'
import { OverallInformationForm } from '../common/OverallInformationForm'
import { SubSection } from '../common/Section'

type TreatmentPlanComprehensiveFieldsProps = {
  viewOnly?: boolean
}

const TreatmentPlanComprehensiveFields = ({ viewOnly }: TreatmentPlanComprehensiveFieldsProps) => (
  <>
    <OverallInformationForm />
    <SubSection title="Treatment Plan">
      <FastField
        component={CustomTextField}
        label="Presenting Problem"
        placeholder="Reason for seeking care or reason for updating treatment plan."
        fullWidth
        multiline
        name="PRESENTING_PROBLEM"
        minRows={4}
      />
      <FastField
        component={CustomTextField}
        label="Long Term Goal"
        placeholder="Please describe the participant's long-term treatment goal."
        fullWidth
        multiline
        name="LONG_TERM_GOAL"
        minRows={4}
      />
    </SubSection>

    <SubSection title="Short Term Goal">
      <FastField
        component={CustomTextField}
        label="S - Specific (clear, concrete, focused)"
        placeholder="Be precise: What will the patient do differently? What behavior, coping skill, or specific change in functioning are you targeting?"
        fullWidth
        multiline
        name="SHORT_TERM_GOAL.SPECIFIC"
        minRows={2}
      />
      <FastField
        component={CustomTextField}
        label="M - Measurable (tracked/quantified objectively)"
        placeholder="How will success be measured - Frequency of behavior, rating scales, percentages, days of the week, etc. It MUST be objective and not a feeling."
        fullWidth
        multiline
        name="SHORT_TERM_GOAL.MEASURABLE"
        minRows={2}
      />
      <FastField
        component={CustomTextField}
        label="A - Achievable (realistic for participant)"
        fullWidth
        name="SHORT_TERM_GOAL.ACHIEVABLE"
      />
      <FastField
        component={CustomTextField}
        label="R - Relevant (clearly connected to diagnosis and reason for treatment)"
        placeholder="Connect the goal directly to the patient's diagnosis or symptoms (e.g., anxiety, depression, sleep disturbance, or functional challenges)."
        fullWidth
        multiline
        name="SHORT_TERM_GOAL.RELEVANT"
        minRows={2}
      />
      <FastField
        component={CustomTextField}
        label="T - Time-bound (specific timeline)"
        placeholder="Include a specific timeframe for the goal. How long will it take to achieve the goal?"
        fullWidth
        name="SHORT_TERM_GOAL.TIME_BOUND"
      />
      <FastField
        component={CustomTextField}
        label="SMART Goal Statement"
        placeholder="Write the finished SMART goal here, blending all of the above pieces into a clear and complete sentence."
        fullWidth
        multiline
        name="SHORT_TERM_GOAL.GOAL_STATEMENT"
        minRows={2}
      />
    </SubSection>

    <SubSection title="Billing and Diagnosis Codes">
      <BillingCodes name="BILLING_CODES" viewOnly={viewOnly} />
    </SubSection>

    <SubSection title="Interventions and participant agreement">
      <Interventions />

      <FastField
        component={CustomTextField}
        label="Strengths"
        fullWidth
        multiline
        name="INTERVENTIONS_AND_PARTICIPANT_AGREEMENT.STRENGTHS"
        minRows={4}
      />

      <FastField
        component={CustomTextField}
        label="Other recommendations and notes"
        fullWidth
        multiline
        name="INTERVENTIONS_AND_PARTICIPANT_AGREEMENT.OTHER"
        minRows={4}
      />

      <FastField
        fullWidth
        component={CustomCheckboxWithoutInitialValue}
        name="INTERVENTIONS_AND_PARTICIPANT_AGREEMENT.PARTICIPANT_AGREEMENT"
        label="Participant agrees to the discussed treatment plan"
      />
    </SubSection>
  </>
)

export default TreatmentPlanComprehensiveFields
